import * as z from "zod/v4";

/**
 * Recommendation lifecycle enums
 */

export const recommendationStatusValues = [
  "active",
  "snoozed",
  "dismissed",
  "accepted",
  "superseded",
  "expired",
] as const;
export type RecommendationStatusValue =
  (typeof recommendationStatusValues)[number];

export const recommendationStatusSchema = z.enum(recommendationStatusValues);
export const recommendationStatuses = recommendationStatusSchema.options;
export type RecommendationStatus = z.infer<typeof recommendationStatusSchema>;

export const actionableRecommendationStatuses: readonly RecommendationStatus[] =
  ["active", "snoozed"];

/**
 * Rule configuration enums
 */

export const recommendationUrgencyValues = [
  "overdue",
  "critical",
  "urgent",
  "attention",
  "healthy",
] as const;
export type RecommendationUrgencyValue =
  (typeof recommendationUrgencyValues)[number];

export const recommendationUrgencySchema = z.enum(recommendationUrgencyValues);
export const recommendationUrgencies = recommendationUrgencySchema.options;
export type RecommendationUrgency = z.infer<typeof recommendationUrgencySchema>;

export const recommendationOptimizationValues = [
  "fewest_suppliers",
  "lowest_cost",
  "fastest_stockout",
  "balanced",
] as const;
export type RecommendationOptimizationValue =
  (typeof recommendationOptimizationValues)[number];

export const recommendationOptimizationSchema = z.enum(
  recommendationOptimizationValues,
);
export const recommendationOptimizations =
  recommendationOptimizationSchema.options;
export type RecommendationOptimization = z.infer<
  typeof recommendationOptimizationSchema
>;

export const recommendationScheduleIntervalUnitValues = [
  "day",
  "week",
  "month",
] as const;
export const recommendationScheduleIntervalUnitSchema = z.enum(
  recommendationScheduleIntervalUnitValues,
);
export const recommendationScheduleIntervalUnits =
  recommendationScheduleIntervalUnitSchema.options;
export type RecommendationScheduleIntervalUnit = z.infer<
  typeof recommendationScheduleIntervalUnitSchema
>;

export const recommendationGroupByValues = [
  "none",
  "product_family",
  "supplier",
  "urgency",
] as const;
export type RecommendationGroupBy =
  (typeof recommendationGroupByValues)[number];

export const draftPOGroupingValues = ["supplier"] as const;
export type DraftPOGrouping = (typeof draftPOGroupingValues)[number];

/**
 * Scheduling data
 */

export interface RecommendationScheduleFields {
  scheduleIntervalCount: number | null;
  scheduleIntervalUnit: RecommendationScheduleIntervalUnit | null;
}

export type NormalizedRecommendationSchedule =
  | { scheduleIntervalCount: null; scheduleIntervalUnit: null }
  | {
      scheduleIntervalCount: number;
      scheduleIntervalUnit: RecommendationScheduleIntervalUnit;
    };

/**
 * Recommendation list DTOs
 */

export interface RecommendationListItem {
  id: string;
  productId: string;
  productTitle: string;
  sku: string | null;
  productFamilyId: string | null;
  productFamilyTitle: string;
  productFamilyImageUrl: string | null;
  supplierId: string;
  supplierName: string;
  status: RecommendationStatus;
  urgency: RecommendationUrgency;
  suggestedQuantity: number;
  unitCost: number | null;
  currency: string;
  riskPerDay: number | null;
  riskUnitsPerDay: number | null;
  orderByDate: Temporal.PlainDate | null;
  projectedStockoutDate: Temporal.PlainDate | null;
  snoozedUntil: Date | null;
  linkedPurchaseOrderId: string | null;
}

export interface RecommendationListGroup {
  groupKey: string;
  groupBy: Exclude<RecommendationGroupBy, "none">;
  label: string;
  productFamilyId: string | null;
  productFamilyTitle: string | null;
  productFamilyImageUrl: string | null;
  supplierId: string | null;
  supplierName: string | null;
  urgency: RecommendationUrgency | null;
  currency: string | null;
  suggestedQuantity: number;
  recommendationCount: number;
  estimatedTotalCost: number;
  riskPerDay: number | null;
  riskUnitsPerDay: number | null;
  earliestOrderByDate: Temporal.PlainDate | null;
  earliestProjectedStockoutDate: Temporal.PlainDate | null;
}

/**
 * Rule DTOs
 */

export interface RecommendationRule {
  id: string;
  name: string;
  enabled: boolean;
  scopeAllSuppliers: boolean;
  optimization: RecommendationOptimization | null;
  coverageDays: number | null;
  urgency: RecommendationUrgency[] | null;
  spendCapFractional: number | null;
  minLineCostFractional: number | null;
  scheduleIntervalCount: number | null;
  scheduleIntervalUnit: RecommendationScheduleIntervalUnit | null;
  supplierIds: string[];
  lastRunAt: Date | null;
  nextRunAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface RuleWriteInput {
  name: string;
  enabled: boolean;
  scopeAllSuppliers: boolean;
  optimization: RecommendationOptimization | null;
  coverageDays: number | null;
  urgency: RecommendationUrgency[] | null;
  spendCapFractional: number | null;
  minLineCostFractional: number | null;
  scheduleIntervalCount: number | null;
  scheduleIntervalUnit: RecommendationScheduleIntervalUnit | null;
  supplierIds: string[];
}

export type RuleUpdateInput = RuleWriteInput;

/**
 * Generation data
 */

export interface RecommendationGenerationRule
  extends RecommendationScheduleFields {
  id: bigint;
  enabled: boolean;
  optimization: RecommendationOptimization | null;
  urgency: RecommendationUrgency[] | null;
  spendCapFractional: number | null;
  minLineCostFractional: number | null;
}

export interface RecommendationGenerationCandidate {
  product_id: bigint;
  product_title: string;
  sku: string | null;
  product_family_id: bigint | null;
  supplier_id: bigint;
  supplier_name: string;
  suggested_quantity: bigint;
  unit_cost_fractional: bigint | null;
  currency: string;
  risk_units_per_day: bigint | null;
  order_by_date: Date | null;
  projected_stockout_date: Date | null;
}

export interface ExistingRecommendationForGeneration {
  id: bigint;
  rule_id: bigint | null;
  product_id: bigint;
  supplier_id: bigint;
  suggested_quantity: bigint;
  unit_cost_fractional: bigint | null;
  currency: string;
  risk_per_day_fractional: bigint | null;
  risk_units_per_day: bigint | null;
  order_by_date: Date | null;
  projected_stockout_date: Date | null;
}

export interface PlannedRecommendationCreate {
  candidate: RecommendationGenerationCandidate;
  riskPerDayFractional: bigint | null;
  supersededRecommendationIds: bigint[];
}

export interface RecommendationGenerationPlan {
  shouldRun: boolean;
  candidatesToCreate: PlannedRecommendationCreate[];
  recommendationIdsToExpire: bigint[];
  nextRunAt: Date | null;
}

/**
 * Draft PO grouping data
 */

export interface RecommendationSelectionCandidate {
  supplierId: string;
  supplierName: string;
  urgency: RecommendationUrgency;
  suggestedQuantity: number | bigint;
  unitCostFractional: bigint | null;
  currency: string;
  orderByDate: Temporal.PlainDate | null;
  projectedStockoutDate: Temporal.PlainDate | null;
}

export interface DraftPOCandidate extends RecommendationSelectionCandidate {
  id: string;
  status: RecommendationStatus;
  suggestedQuantity: number;
  unitCost: number | null;
}

export interface DraftPOGroup<TCandidate extends DraftPOCandidate> {
  groupKey: string;
  supplierId: string;
  supplierName: string;
  currency: string;
  estimatedTotal: number;
  variants: TCandidate[];
}
